import { useEffect, useState, type ReactNode } from 'react';
import { planRows } from './planTree';
import type { StructurePlanTree } from '../types';

/**
 * What the planner is doing while a reply runs. A whole-site build takes
 * several minutes with no chat text, so a bare "AI is working…" read as a
 * hang. Everything here comes from the saved plan (start time, draft so far),
 * so it reads the same after a page reload.
 */

interface Props {
  /** When the running turn started (the user's message); ISO time. */
  workingSince?: string | null;
  /** 'thorough' for a thorough build; anything else reads as quick. */
  tier?: string | null;
  tree: StructurePlanTree | null;
  hasBrief: boolean;
  children?: ReactNode;
}

/** "just now", "1 min", "12 min". Exported for tests. */
export function elapsedLabel(since: string | null | undefined, now: number): string | null {
  if (!since) return null;
  const start = Date.parse(since);
  if (Number.isNaN(start)) return null;
  const mins = Math.floor((now - start) / 60000);
  return mins < 1 ? 'just now' : `${mins} min ago`;
}

/** The draft's size in the words the panel uses. Exported for tests. */
export function draftSoFar(tree: StructurePlanTree | null): string | null {
  const rows = planRows(tree);
  const sections = rows.filter((r) => r.depth === 0).length;
  if (!sections) return null;
  const subSections = rows.filter((r) => r.depth > 0 && !r.hasChildren).length;
  const keywords = rows.reduce((n, r) => n + r.seedCount, 0);
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  return `${plural(sections, 'section')}, ${plural(subSections, 'sub-section')}, ${plural(keywords, 'keyword')}`;
}

export default function PlannerProgress({
  workingSince,
  tier,
  tree,
  hasBrief,
  children,
}: Props) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(t);
  }, []);

  const started = elapsedLabel(workingSince, now);
  const soFar = draftSoFar(tree);

  return (
    <div
      role="status"
      className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 p-3 space-y-1 text-xs text-gray-700 dark:text-gray-300"
    >
      <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
        <span className="inline-block h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />
        Working{started ? ` · started ${started}` : '…'}
      </div>
      {tier === 'thorough' ? (
        <p>
          A thorough build takes about 10 to 20 minutes. It reads all of the research
          room&apos;s searches and looks up each sub-section before writing its own.
        </p>
      ) : hasBrief ? (
        <p>
          Building the whole site takes several minutes: it reads all of the research
          room&apos;s searches. Smaller changes take under a minute.
        </p>
      ) : (
        <p>Reading the research room and choosing questions to ask you. This takes about a minute.</p>
      )}
      {soFar && (
        <p>
          The draft so far: {soFar}. It fills in on the left as the work goes.
        </p>
      )}
      <p className="text-gray-500 dark:text-gray-400">
        You can refresh or leave this page. The work carries on, and the result will be here
        when you come back.
      </p>
      {children}
    </div>
  );
}
